import Styles from '../../AutopilotPlansModal.module.scss'

import { Heading } from 'blaze-ui/Heading/Heading'

import { Modal } from 'almanac-ui/Modal'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { ArrowLeft, InformationCircleContained, Loader1 } from 'blaze-ui/icons'

import { CurrentPlan, PlanDetails, nameMap } from './components/PlanDetails/PlanDetails'
import { Text } from 'blaze-ui/Text/Text'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { Button } from 'blaze-ui/Button'
import { useRootStore } from 'stores/RootStore'
import { AutopilotPlan } from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/types'
import { formatPrice, calculateMonthlyExtraAccountCharges } from 'components/Settings/TieredBilling/utils'
import dayjs from 'dayjs'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

export const findMonthlyPrice = (plan: AutopilotPlan, intervalCount = 1, currentSubscription?: any) => {
  const monthlyPrice = plan.prices.find((p) => p.recurring.interval_count === intervalCount)
  let totalPrice = monthlyPrice ? Math.ceil(monthlyPrice.unit_amount / intervalCount / 100) : 0

  // Add monthly extra account charges
  const extraAccountCharges = calculateMonthlyExtraAccountCharges(currentSubscription)
  totalPrice += Math.ceil(extraAccountCharges)

  return totalPrice
}

export const Plans = ({
  onBack,
  onCancelSubscription,
  onSelectPlan,
  isProcessing,
}: {
  onBack: () => void
  onCancelSubscription: () => void
  onSelectPlan: (plan: AutopilotPlan | null) => void
  isProcessing: boolean
}) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const { tierUsageStore } = useRootStore()
  const { autopilotPlans, currentSubscription } = tierUsageStore
  const workspace = useCurrentWorkspace()

  if (!currentSubscription) {
    return null
  }

  const price = currentSubscription.prices[0].unit_amount
  const intervalCount = currentSubscription.prices[0].recurring.interval_count

  // Calculate total price including extra accounts
  const basePriceMonthly = price / intervalCount / 100
  const extraAccountCharges = calculateMonthlyExtraAccountCharges(currentSubscription)
  const totalMonthlyPrice = basePriceMonthly + extraAccountCharges

  const currentPlan: CurrentPlan = !workspace?.is_ap_freemium
    ? {
        key: currentSubscription?.plan,
        length: currentSubscription?.prices[0].recurring.interval_count,
        monthlyPrice: formatPrice(totalMonthlyPrice),
      }
    : {
        key: PaymentPlanKey.Free,
        length: 1,
        monthlyPrice: 0,
      }
  const starterPlan = autopilotPlans.find((plan) => plan.key === PaymentPlanKey.BlazeAutopilotStarter)
  const isOnPioneerPlan = currentPlan.key === PaymentPlanKey.VirtualMarketer
  const growthPlan = autopilotPlans.find((plan) => plan.key === PaymentPlanKey.BlazeAutopilotGrowth)
  const enterprisePlan = autopilotPlans.find((plan) => plan.key === PaymentPlanKey.BlazeAutopilotEnterprise)
  const isTrialing = currentSubscription?.status === 'trialing'

  const showInfo = currentSubscription.cancel_at || isTrialing

  return (
    <>
      <Modal.Header>
        <div className={Styles.header}>
          <div className={Styles.left}>
            <div className={Styles.backAndTitle}>
              {!isSystemRedesignEnabled && (
                <IconButton icon={ArrowLeft} onPress={onBack} variant="tertiary" size="xs" />
              )}
              <Heading level={3}>Choose your Blaze plan</Heading>
            </div>
            {showInfo && (
              <div className={Styles.info}>
                <InformationCircleContained color="var(--dark-60)" />
                {currentSubscription.cancel_at && (
                  <Text color="var(--dark-60)">
                    Your {nameMap[currentSubscription.plan]} plan ends{' '}
                    {dayjs(currentSubscription.cancel_at).format('MMMM D')} at{' '}
                    {dayjs(currentSubscription.cancel_at).format('h:mm A')}; Free plan starts right after.
                  </Text>
                )}
                {isTrialing && !currentSubscription.cancel_at && (
                  <Text color="var(--dark-60)">
                    Your {nameMap[currentSubscription.plan]} trial ends in {currentSubscription.trial_days_left} days;
                    Free plan starts right after.
                  </Text>
                )}
              </div>
            )}
          </div>
          {!workspace?.is_ap_freemium && !isTrialing && (
            <Button variant="tertiary" onPress={onCancelSubscription}>
              Cancel Subscription
            </Button>
          )}
        </div>
      </Modal.Header>
      <Modal.Content className={Styles.content} withoutFooter={isSystemRedesignEnabled}>
        {isOnPioneerPlan && (
          <div className={Styles.pioneerCta}>
            <div className={Styles.header}>
              <Loader1 color="var(--light-100)" />
              <Heading level={4} color="var(--light-100)">
                You're on the Pioneer plan
              </Heading>
            </div>
            <Text color="var(--light-100)">
              You have unlimited channels, posts, seats, and crossposting. Since the Pioneer plan is for early Autopilot
              adopters, it is not available again once canceled or changed.
            </Text>
          </div>
        )}
        <div className={Styles.plansContent}>
          <div className={Styles.individualPlans}>
            <div className={Styles.header}>
              <Text variant="smallList">Individual</Text>
            </div>
            <div className={Styles.plans}>
              {currentSubscription?.plan && (
                <PlanDetails
                  planKey={PaymentPlanKey.Free}
                  monthlyPrice={0}
                  currentPlan={currentPlan}
                  onSelectPlan={() => onSelectPlan({ key: PaymentPlanKey.Free } as AutopilotPlan)}
                  isTrialing={isTrialing}
                  isProcessing={isProcessing}
                />
              )}
              {starterPlan && currentSubscription?.plan && (
                <PlanDetails
                  planKey={starterPlan.key}
                  monthlyPrice={findMonthlyPrice(starterPlan, 1, currentSubscription)}
                  currentPlan={currentPlan}
                  onSelectPlan={() => onSelectPlan(starterPlan)}
                  isTrialing={isTrialing}
                  isProcessing={isProcessing}
                />
              )}
            </div>
          </div>
          <div className={Styles.teamPlans}>
            <div className={Styles.header}>
              <Text variant="smallList">Team</Text>
            </div>
            <div className={Styles.plans}>
              {growthPlan && currentSubscription?.plan && (
                <PlanDetails
                  planKey={growthPlan.key}
                  monthlyPrice={findMonthlyPrice(growthPlan, 1, currentSubscription)}
                  currentPlan={currentPlan}
                  onSelectPlan={() => onSelectPlan(growthPlan)}
                  isTrialing={currentSubscription?.status === 'trialing'}
                  isProcessing={isProcessing}
                />
              )}
              {enterprisePlan && currentSubscription?.plan && (
                <PlanDetails
                  planKey={enterprisePlan.key}
                  monthlyPrice={findMonthlyPrice(enterprisePlan, 1, currentSubscription)}
                  currentPlan={currentPlan}
                  onSelectPlan={() => onSelectPlan(enterprisePlan)}
                  isTrialing={currentSubscription?.status === 'trialing'}
                  isProcessing={isProcessing}
                />
              )}
            </div>
          </div>
        </div>
      </Modal.Content>
    </>
  )
}
